"use client";

import { c as _c } from "react/compiler-runtime";
import type { ApiIdentity } from "@/generated/models/ApiIdentity";
import type { WaveMessages } from "@/contexts/wave/hooks/types";
import type { Listener as WaveMessagesListener } from "@/contexts/wave/hooks/useWaveMessagesStore";
import { useMyStream } from "@/contexts/wave/MyStreamContext";
import { useCallback, useEffect, useState } from "react";
import { getLatestEditableChatDrop } from "../utils/getLatestEditableChatDrop";
type LatestEditableChatDropTarget = {
  readonly id: string;
  readonly serialNo: number;
};
type ConnectedProfile = Pick<ApiIdentity, "id" | "handle"> | null | undefined;
type TargetState = {
  readonly waveId: string;
  readonly connectedProfileId: string | null;
  readonly connectedProfileHandle: string | null;
  readonly isProxyMode: boolean;
  readonly target: LatestEditableChatDropTarget | null;
};
const getTargetFromWaveMessages = ({
  waveMessages,
  waveId,
  connectedProfileId,
  connectedProfileHandle,
  isProxyMode
}: {
  readonly waveMessages: WaveMessages | undefined;
  readonly waveId: string;
  readonly connectedProfileId: string | null;
  readonly connectedProfileHandle: string | null;
  readonly isProxyMode: boolean;
}): LatestEditableChatDropTarget | null => {
  const connectedProfile = connectedProfileId || connectedProfileHandle ? {
    id: connectedProfileId ?? "",
    handle: connectedProfileHandle ?? ""
  } : null;
  const latestDrop = getLatestEditableChatDrop({
    drops: waveMessages?.drops,
    waveId,
    connectedProfile,
    isProxyMode
  });
  return latestDrop ? {
    id: latestDrop.id,
    serialNo: latestDrop.serial_no
  } : null;
};
export function useLatestEditableChatDropTarget(t0) {
  const $ = _c(27);
  const {
    waveId,
    connectedProfile,
    isProxyMode
  } = t0;
  const {
    waveMessagesStore
  } = useMyStream();
  const {
    getData,
    subscribe,
    unsubscribe
  } = waveMessagesStore;
  const connectedProfileId = connectedProfile?.id ?? null;
  const connectedProfileHandle = connectedProfile?.handle ?? null;
  const hasEditableProfile = Boolean(connectedProfileId || connectedProfileHandle);
  let t1;
  if ($[0] !== connectedProfileHandle || $[1] !== connectedProfileId || $[2] !== isProxyMode || $[3] !== waveId) {
    t1 = waveMessages => getTargetFromWaveMessages({
      waveMessages,
      waveId,
      connectedProfileId,
      connectedProfileHandle,
      isProxyMode
    });
    $[0] = connectedProfileHandle;
    $[1] = connectedProfileId;
    $[2] = isProxyMode;
    $[3] = waveId;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const getTarget = t1;
  let t2;
  if ($[5] !== connectedProfileHandle || $[6] !== connectedProfileId || $[7] !== getTarget || $[8] !== isProxyMode || $[9] !== waveId) {
    t2 = waveMessages_0 => ({
      waveId,
      connectedProfileId,
      connectedProfileHandle,
      isProxyMode,
      target: getTarget(waveMessages_0)
    });
    $[5] = connectedProfileHandle;
    $[6] = connectedProfileId;
    $[7] = getTarget;
    $[8] = isProxyMode;
    $[9] = waveId;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  const createTargetState = t2;
  let t3;
  if ($[11] !== createTargetState || $[12] !== getData || $[13] !== waveId) {
    t3 = () => createTargetState(getData(waveId));
    $[11] = createTargetState;
    $[12] = getData;
    $[13] = waveId;
    $[14] = t3;
  } else {
    t3 = $[14];
  }
  const [targetState, setTargetState] = useState(t3);
  let t4;
  let t5;
  if ($[15] !== createTargetState || $[16] !== hasEditableProfile || $[17] !== isProxyMode || $[18] !== subscribe || $[19] !== unsubscribe || $[20] !== waveId) {
    t4 = () => {
      const updateTarget = waveMessages_1 => {
        const nextState = createTargetState(waveMessages_1);
        setTargetState(currentState => {
          if (currentState.waveId === nextState.waveId && currentState.connectedProfileId === nextState.connectedProfileId && currentState.connectedProfileHandle === nextState.connectedProfileHandle && currentState.isProxyMode === nextState.isProxyMode && currentState.target?.id === nextState.target?.id && currentState.target?.serialNo === nextState.target?.serialNo) {
            return currentState;
          }
          return nextState;
        });
      };
      const listener = waveMessages_2 => {
        updateTarget(waveMessages_2);
      };
      if (!hasEditableProfile || isProxyMode) {
        updateTarget(undefined);
        return;
      }
      subscribe(waveId, listener);
      return () => {
        unsubscribe(waveId, listener);
      };
    };
    t5 = [createTargetState, hasEditableProfile, isProxyMode, subscribe, unsubscribe, waveId];
    $[15] = createTargetState;
    $[16] = hasEditableProfile;
    $[17] = isProxyMode;
    $[18] = subscribe;
    $[19] = unsubscribe;
    $[20] = waveId;
    $[21] = t4;
    $[22] = t5;
  } else {
    t4 = $[21];
    t5 = $[22];
  }
  useEffect(t4, t5);
  if (targetState.waveId === waveId && targetState.connectedProfileId === connectedProfileId && targetState.connectedProfileHandle === connectedProfileHandle && targetState.isProxyMode === isProxyMode) {
    return targetState.target;
  }
  let t6;
  if ($[23] !== createTargetState || $[24] !== getData || $[25] !== waveId) {
    t6 = createTargetState(getData(waveId));
    $[23] = createTargetState;
    $[24] = getData;
    $[25] = waveId;
    $[26] = t6;
  } else {
    t6 = $[26];
  }
  return t6.target;
}